import React, { FormEvent, useEffect, useState } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import type { UserDisplaySettings } from '@/models/user';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Save, Zap } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';

type ActiveWorkspace = UserDisplaySettings['activeWorkspace'];

interface WebhookSettingsCardProps {
  workspaceId: string;
  activeWorkspace: ActiveWorkspace;
  onUpdate: () => Promise<void>;
}

export const WebhookSettingsCard: React.FC<WebhookSettingsCardProps> = ({
  workspaceId,
  activeWorkspace,
  onUpdate,
}) => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isPro = activeWorkspace.planDetails?.planId === 'pro';

  useEffect(() => {
    setWebhookUrl(activeWorkspace.webhookUrl || '');
  }, [activeWorkspace.webhookUrl]);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!isPro) {
      sonnerToast.message('Upgrade to Growth to enable outbound webhooks.');
      return;
    }
    setIsSaving(true);
    try {
      const updateFn = httpsCallable(firebaseFunctions, 'updateWorkspaceSettings');
      await updateFn({
        workspaceId,
        webhookUrl: webhookUrl.trim() || null,
      });
      sonnerToast.success('Webhook URL saved.');
      await onUpdate();
    } catch (error: any) {
      sonnerToast.error(`Failed to save webhook URL: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Automation Webhook</CardTitle>
            <CardDescription className="text-slate-600">
              Send a POST request to your CRM/automation tool when a financing quote is paid.
            </CardDescription>
          </div>
          <Badge variant={isPro ? 'default' : 'secondary'} className="rounded-full">{isPro ? 'Growth' : 'Upgrade Required'}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="webhook-url" className="text-sm font-medium text-slate-900">
              Webhook URL
            </label>
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://hooks.zapier.com/..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={isSaving || !isPro}
              className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
            />
            <p className="text-xs text-muted-foreground">
              Fires on quote status transition to <code>paid</code>. Empty value disables webhook delivery.
            </p>
          </div>

          {!isPro && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-700">
              Growth plan includes webhook automations (Zapier, GoHighLevel, custom CRM endpoints).
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSaving}>
              {isPro ? <Save className="mr-2 h-4 w-4" /> : <Zap className="mr-2 h-4 w-4" />}
              {isPro ? 'Save Webhook' : 'Upgrade to Growth'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
